import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import {
  fetchNotificationPreferences,
  fetchWorkspaceSettings,
  saveNotificationPreference,
  saveProfileSettings,
  saveWorkspaceSettings,
  settingsKeys,
} from './queries';
import type { NotificationEvent } from '../notifications/types';
import type { WorkspaceSettings } from './settingsLens';

function useMe(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

export function useNotificationPreferences() {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: settingsKeys.preferences(),
    queryFn: () => fetchNotificationPreferences(client),
    enabled: access.status === 'approved',
    retry: false,
  });
}

export function useSaveNotificationPreference() {
  const { client } = useAuth();
  const userId = useMe();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      eventType: NotificationEvent;
      slackEnabled: boolean;
      inAppAlertEnabled: boolean;
    }) => saveNotificationPreference(client, { ...input, userId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.preferences() });
    },
  });
}

export function useWorkspaceSettings() {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: settingsKeys.workspace(),
    queryFn: () => fetchWorkspaceSettings(client),
    enabled: access.status === 'approved',
    // Read by more than this screen eventually — these are defaults the whole
    // app applies — so it is worth not refetching them constantly.
    staleTime: 60_000,
    retry: false,
  });
}

export function useSaveWorkspaceSettings() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<WorkspaceSettings>) => saveWorkspaceSettings(client, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.workspace() });
    },
  });
}

export function useSaveProfileSettings() {
  const { client } = useAuth();
  const userId = useMe();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      displayName?: string;
      timeZone?: string;
      shiftStartHour?: number;
      shiftLengthHours?: number;
    }) => saveProfileSettings(client, { ...input, userId }),
    onSuccess: () => {
      // The profile is read by the auth provider and by every avatar on the
      // page, so this invalidates broadly rather than one key.
      void queryClient.invalidateQueries();
    },
  });
}
